import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip,
  CircularProgress, Divider, LinearProgress, Alert
} from '@mui/material';
import {
  Phone, AccountBalance, TrendingUp, CheckCircle, Cancel
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../../services/api';
import { QuickDial } from '../../components/common/QuickDial';
import { useAuthStore } from '../../store/auth';

const StatCard = ({ title, value, sub, icon, color }: any) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
          <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Box sx={{ bgcolor: `${color || 'primary'}.light`, p: 1.5, borderRadius: 2, opacity: 0.8 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const CustomerDashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/billing/weekly-summary')
      .then(({ data }) => setSummary(data))
      .catch(e => setError(e.response?.data?.message || 'Failed to load summary'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const balance = summary?.balance || {};
  const weekly: any[] = summary?.weekly || [];
  const totals = weekly.reduce((a: any, w: any) => ({
    calls: a.calls + (w.total_calls || 0),
    answered: a.answered + (w.answered_calls || 0),
    failed: a.failed + (w.failed_calls || 0),
    cost: a.cost + parseFloat(w.total_cost || 0),
    seconds: a.seconds + (w.total_seconds || 0),
  }), { calls: 0, answered: 0, failed: 0, cost: 0, seconds: 0 });

  const asr = totals.calls > 0 ? ((totals.answered / totals.calls) * 100).toFixed(1) : '0';
  const chartData = weekly.map(w => ({
    week: `W${w.week_start?.slice(5, 10)}`,
    Calls: w.total_calls || 0,
    Cost: parseFloat(w.total_cost || 0).toFixed(2),
    Minutes: Math.round((w.total_seconds || 0) / 60),
  }));

  const usedPct = balance.credit_limit > 0
    ? Math.min(100, ((balance.credit_limit - balance.balance) / balance.credit_limit) * 100)
    : 0;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Welcome, {user?.first_name || user?.username}</Typography>
        <Typography variant="body2" color="text.secondary">Your account overview — last 8 weeks</Typography>
      </Box>

      {/* Balance card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Current Balance</Typography>
              <Typography variant="h3" fontWeight={700}>${Number(balance.balance || 0).toFixed(2)}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>{balance.currency || 'USD'} · {balance.billing_type || 'prepaid'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Credit Limit</Typography>
              <Typography variant="h5" fontWeight={600}>${Number(balance.credit_limit || 0).toFixed(2)}</Typography>
              <LinearProgress variant="determinate" value={usedPct} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: usedPct > 80 ? '#ff5252' : 'white' } }} />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>{usedPct.toFixed(0)}% used</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Reserved</Typography>
              <Typography variant="h5" fontWeight={600}>${Number(balance.reserved || 0).toFixed(2)}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>Active call hold</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Dial */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <QuickDial />
        </Grid>
      </Grid>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Calls (8 weeks)" value={totals.calls.toLocaleString()}
            icon={<Phone sx={{ color: 'primary.main' }} />} color="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Answered Calls" value={totals.answered.toLocaleString()}
            sub={`ASR: ${asr}%`} icon={<CheckCircle sx={{ color: 'success.main' }} />} color="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Failed Calls" value={totals.failed.toLocaleString()}
            icon={<Cancel sx={{ color: 'error.main' }} />} color="error" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Cost (8 weeks)" value={`$${totals.cost.toFixed(2)}`}
            sub={`${Math.round(totals.seconds / 60)} minutes`}
            icon={<TrendingUp sx={{ color: 'warning.main' }} />} color="warning" />
        </Grid>
      </Grid>

      {/* Weekly charts */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Weekly Call Volume</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="Calls" fill="#1976d2" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Weekly Charges</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => [`$${v}`, 'Cost']} />
                  <Bar dataKey="Cost" fill="#43a047" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        {/* Weekly table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Weekly Breakdown</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      {['Week', 'Total Calls', 'Answered', 'Failed', 'Minutes', 'Cost'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.map((w, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{w.week_start} → {w.week_end}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>{w.total_calls || 0}</td>
                        <td style={{ padding: '8px 12px', color: '#2e7d32' }}>{w.answered_calls || 0}</td>
                        <td style={{ padding: '8px 12px', color: '#c62828' }}>{w.failed_calls || 0}</td>
                        <td style={{ padding: '8px 12px' }}>{Math.round((w.total_seconds || 0) / 60)}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600 }}>${parseFloat(w.total_cost || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
